import { storeWrapper } from '../storeWrapper'
import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { handleFormEvoTokenExpired } from '@repo/utils/handleFormEvoTokenExpired'

const initialState = {
  formCategories: []
}

export const useFormsStore = storeWrapper('useFormsStore', (set) => ({
  ...initialState,
  getAllFormsBySearchCriteria: async ({
    startAtIndex,
    pageSize,
    orderByColumns,
    formCategoryId,
    name,
    description,
    workTypeId
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `getAllFormsBySearchCriteria-${formCategoryId}-${name}-${description}-${workTypeId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'matters',
              endpoint: 'Form/GetAllBySearchCriteria',
              data: {
                startAtIndex: startAtIndex ?? 0,
                pageSize: pageSize ?? 25,
                orderByColumns: orderByColumns ?? null,
                formCategoryId: formCategoryId ?? null,
                name: name ?? null,
                description: description ?? null,
                workTypeId: workTypeId ?? null
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching forms:', error)
            reject(error)
          }
        }
      })
    })
  },
  getAllFormsByWorkTypeId: async ({ workTypeId, startAtIndex, pageSize }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `getAllFormsByWorkTypeId-${workTypeId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'matters',
              endpoint: 'Form/GetAllByWorkTypeId',
              data: {
                workTypeId: workTypeId ?? null,
                startAtIndex: startAtIndex ?? 0,
                pageSize: pageSize ?? 25
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching forms by work type ID:', error)
            reject(error)
          }
        }
      })
    })
  },
  getFormPreviewUrls: async ({ formId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `getFormPreviewUrls-${formId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `Form/GetFormPreviewUrls`,
              data: {
                formId: formId ?? null
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching form preview URLs:', error)
            reject(error)
          }
        }
      })
    })
  },
  createForm: async ({
    formId,
    formCategoryId,
    name,
    xsdName,
    description,
    eSubmittable,
    eSignature,
    countryTags,
    revision,
    revisionNotes,
    createdDate,
    updatedDate
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `createForm-${formId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'matters',
              endpoint: 'Form/Create',
              data: {
                formId: formId ?? null,
                formCategoryId: formCategoryId ?? null,
                name: name ?? null,
                xsdName: xsdName ?? null,
                description: description ?? null,
                eSubmittable: eSubmittable ?? null,
                eSignature: eSignature ?? null,
                countryTags: countryTags ?? null,
                revision: revision ?? null,
                revisionNotes: revisionNotes ?? null,
                createdDate: createdDate ?? null,
                updatedDate: updatedDate ?? null
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error creating form:', error)
            reject(error)
          }
        }
      })
    })
  },
  updateForm: async ({
    formId,
    formCategoryId,
    name,
    xsdName,
    description,
    eSubmittable,
    eSignature,
    countryTags,
    revision,
    revisionNotes,
    createdDate,
    updatedDate
  }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `updateForm-${formId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'matters',
              endpoint: 'Form/Update',
              data: {
                formId: formId ?? null,
                formCategoryId: formCategoryId ?? null,
                name: name ?? null,
                xsdName: xsdName ?? null,
                description: description ?? null,
                eSubmittable: eSubmittable ?? null,
                eSignature: eSignature ?? null,
                countryTags: countryTags ?? null,
                revision: revision ?? null,
                revisionNotes: revisionNotes ?? null,
                createdDate: createdDate ?? null,
                updatedDate: updatedDate ?? null
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error updating form:', error)
            reject(error)
          }
        }
      })
    })
  },
  deleteForm: async ({ formId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `deleteForm-${formId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'DELETE',
              service: 'matters',
              endpoint: 'Form/Delete',
              data: {
                formId: formId ?? null
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error deleting form:', error)
            reject(error)
          }
        }
      })
    })
  },
  formExists: async ({ id }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `formExists-${id}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: 'Form/Exists',
              data: {
                id: id ?? null
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error checking if form exists:', error)
            reject(error)
          }
        }
      })
    })
  },
  getForm: async ({ formId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `getForm-${formId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: 'Form/Get',
              data: {
                id: formId ?? null
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching form:', error)
            reject(error)
          }
        }
      })
    })
  },
  getAllForms: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `getAllForms`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: 'Form/GetAll'
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching all forms:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchAllFormCategories: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: 'fetchAllFormCategories',
        delay: 300,
        callback: async () => {
          try {
            const formCategories = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: 'FormCategory/GetAllNested'
            })

            set({ formCategories })
            resolve(formCategories)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching form categories:', error)
            reject(error)
          }
        }
      })
    })
  },
  getFormByDescription: async ({ description, excludedId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `getFormByDescription-${description}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: 'Form/GetByDescription',
              data: {
                description: description ?? null,
                excludedId: excludedId ?? null
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching form by description:', error)
            reject(error)
          }
        }
      })
    })
  },
  updateAllFormWorkTypes: async ({ formIds, workTypeId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `updateAllFormWorkTypes-${(formIds ?? []).join('-')}-${workTypeId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'matters',
              endpoint: `/FormWorkType/UpdateAll?workTypeId=${encodeURIComponent(workTypeId ?? '')}`,
              data: formIds ?? []
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error updating form work types:', error)
            reject(error)
          }
        }
      })
    })
  },
  createFormWorkType: async ({ formId, workTypeId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `createFormWorkType-${formId}-${workTypeId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'POST',
              service: 'matters',
              endpoint: '/FormWorkType/Create',
              data: {
                formId: formId ?? null,
                workTypeId: workTypeId ?? null
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error creating form work type:', error)
            reject(error)
          }
        }
      })
    })
  },
  updateFormWorkType: async ({ formId, workTypeId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `updateFormWorkType-${formId}-${workTypeId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'PUT',
              service: 'matters',
              endpoint: '/FormWorkType/Update',
              data: {
                formId: formId ?? null,
                workTypeId: workTypeId ?? null
              }
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error updating form work type:', error)
            reject(error)
          }
        }
      })
    })
  },
  deleteFormWorkType: async ({ formWorkTypeId }) => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `deleteFormWorkType-${formWorkTypeId}`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'DELETE',
              service: 'matters',
              endpoint: '/FormWorkType/Delete',
              data: {
                id: formWorkTypeId ?? null
              },
              sendAsUrlParams: true
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error deleting form work type:', error)
            reject(error)
          }
        }
      })
    })
  },
  getAllFormWorkTypes: async () => {
    return new Promise((resolve, reject) => {
      debounce({
        id: `getAllFormWorkTypes`,
        delay: 300,
        callback: async () => {
          try {
            const response = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: '/FormWorkType/GetAll'
            })

            resolve(response)
          } catch (error) {
            handleFormEvoTokenExpired(error)
            console.error('Error fetching all form work types:', error)
            reject(error)
          }
        }
      })
    })
  },
  reset: () => set(initialState)
}))
